import React, { useEffect, useRef, useState } from 'react';
import { useMediaDevices } from '../../hooks/useMediaDevices';
import {
  setSavedVideoDeviceId, setSavedAudioDeviceId, setSavedAudioOutputDeviceId,
  getAlwaysShowPreview, setAlwaysShowPreview,
} from '../../utils/deviceStorage';
import './DeviceSelectModal.css';

/**
 * Zoom-style "check your camera/mic before joining" screen. Runs its own
 * short-lived getUserMedia for the preview — the caller only ever sees the
 * final device selection via onConfirm, once this component has released
 * every preview track (pre-join and call streams must never overlap, or
 * some browsers/OSes treat the camera as still busy).
 */
export default function DeviceSelectModal({ isOpen, onConfirm, onCancel, initialDeviceIds }) {
  const [selectedVideoId, setSelectedVideoId] = useState(initialDeviceIds?.videoDeviceId || null);
  const [selectedAudioId, setSelectedAudioId] = useState(initialDeviceIds?.audioDeviceId || null);
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState(initialDeviceIds?.audioOutputDeviceId || null);
  const [alwaysShow, setAlwaysShow] = useState(() => getAlwaysShowPreview());
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mediaReady, setMediaReady] = useState(false);
  const [error, setError] = useState(null);
  const [speakerSupported] = useState(() => typeof document.createElement('video').setSinkId === 'function');

  const previewStreamRef = useRef(null);
  const previewVideoRef = useRef(null);
  const micOnRef = useRef(micOn);
  const camOnRef = useRef(camOn);
  useEffect(() => { micOnRef.current = micOn; }, [micOn]);
  useEffect(() => { camOnRef.current = camOn; }, [camOn]);

  const { cameras, microphones, speakers, refresh } = useMediaDevices();

  const stopPreview = () => {
    const s = previewStreamRef.current;
    if (s) { s.getTracks().forEach(t => t.stop()); }
    previewStreamRef.current = null;
  };

  const startPreview = async (videoId, audioId) => {
    setError(null);
    stopPreview();
    const constraints = {
      video: videoId ? { deviceId: { exact: videoId } } : true,
      audio: audioId ? { deviceId: { exact: audioId } } : true,
    };
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        if (e.name === 'OverconstrainedError' || e.name === 'NotFoundError') {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } else {
          throw e;
        }
      }
      previewStreamRef.current = stream;
      stream.getVideoTracks().forEach(t => { t.enabled = camOnRef.current; });
      stream.getAudioTracks().forEach(t => { t.enabled = micOnRef.current; });
      if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;

      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      if (videoTrack) setSelectedVideoId(videoTrack.getSettings().deviceId || videoId || null);
      if (audioTrack) setSelectedAudioId(audioTrack.getSettings().deviceId || audioId || null);

      setMediaReady(true);
      refresh();
    } catch (e) {
      setError('Camera/mic access denied — check your browser permissions.');
      setMediaReady(true);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setMediaReady(false);
    startPreview(selectedVideoId, selectedAudioId);
    return () => stopPreview();
    // Only re-run when the modal opens — device switches call startPreview
    // directly instead of going through this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    previewStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = next; });
  };

  const toggleCam = () => {
    const next = !camOn;
    setCamOn(next);
    previewStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = next; });
  };

  const handleConfirm = () => {
    stopPreview();
    setSavedVideoDeviceId(selectedVideoId);
    setSavedAudioDeviceId(selectedAudioId);
    setSavedAudioOutputDeviceId(selectedAudioOutputId);
    setAlwaysShowPreview(alwaysShow);
    onConfirm({
      videoDeviceId: selectedVideoId,
      audioDeviceId: selectedAudioId,
      audioOutputDeviceId: selectedAudioOutputId,
    });
  };

  const handleCancel = () => {
    stopPreview();
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal device-select-modal">
        <div className="custom-modal-header">
          <h3>Check your camera &amp; microphone</h3>
          <button className="custom-modal-close" onClick={handleCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="custom-modal-body">
          <div className="device-select-preview-wrap">
            <video
              ref={previewVideoRef}
              autoPlay muted playsInline
              className="device-select-preview-video"
              style={{ opacity: camOn ? 1 : 0.25 }}
            />
            <div className="device-select-preview-controls">
              <button
                className={`device-select-toggle-btn${micOn ? '' : ' off'}`}
                onClick={toggleMic}
                title={micOn ? 'Mute' : 'Unmute'}
              >
                {micOn
                  ? <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z"/><path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5"/></svg>
                  : <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M13 8c0 .564-.094 1.107-.266 1.613l-.814-.814A4 4 0 0 0 12 8V7a.5.5 0 0 1 1 0zm-5 4c.818 0 1.578-.245 2.212-.667l.718.719a5 5 0 0 1-2.43.923V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 1 0v1a4 4 0 0 0 4 4m3-9v4.879l-1-1V3a2 2 0 0 0-3.997-.118l-.845-.845A3.001 3.001 0 0 1 11 3"/><path d="m9.486 10.607-.748-.748A2 2 0 0 1 6 8v-.878l-1-1V8a3 3 0 0 0 4.486 2.607m-7.84-9.253 12 12 .708-.708-12-12z"/></svg>
                }
              </button>
              <button
                className={`device-select-toggle-btn${camOn ? '' : ' off'}`}
                onClick={toggleCam}
                title={camOn ? 'Hide camera' : 'Show camera'}
              >
                {camOn
                  ? <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2z"/></svg>
                  : <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M10.961 12.365a2 2 0 0 0 .522-1.103l3.11 1.382A1 1 0 0 0 16 11.731V4.269a1 1 0 0 0-1.406-.913l-3.111 1.382A2 2 0 0 0 9.5 3H4.272l.714 1H9.5a1 1 0 0 1 1 1v6a1 1 0 0 1-.144.518zM1.428 4.18A1 1 0 0 0 1 5v6a1 1 0 0 0 1 1h5.014l.714 1H2a2 2 0 0 1-2-2V5c0-.675.334-1.272.847-1.634zM15 11.73l-3.5-1.555v-4.35L15 4.269zm-4.407 3.56-10-14 .814-.58 10 14z"/></svg>
                }
              </button>
            </div>
          </div>

          {error && <p className="device-select-error">{error}</p>}

          <div className="device-select-row">
            <label>Camera</label>
            <select value={selectedVideoId || ''} onChange={(e) => startPreview(e.target.value, selectedAudioId)}>
              {cameras.length === 0 && <option value="">No camera found</option>}
              {cameras.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
              ))}
            </select>
          </div>

          <div className="device-select-row">
            <label>Microphone</label>
            <select value={selectedAudioId || ''} onChange={(e) => startPreview(selectedVideoId, e.target.value)}>
              {microphones.length === 0 && <option value="">No microphone found</option>}
              {microphones.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
              ))}
            </select>
          </div>

          {speakerSupported && (
            <div className="device-select-row">
              <label>Speaker</label>
              <select value={selectedAudioOutputId || ''} onChange={(e) => setSelectedAudioOutputId(e.target.value)}>
                {speakers.length === 0 && <option value="">No speaker found</option>}
                {speakers.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Speaker ${i + 1}`}</option>
                ))}
              </select>
            </div>
          )}

          <label className="device-select-always-row">
            <input type="checkbox" checked={alwaysShow} onChange={(e) => setAlwaysShow(e.target.checked)} />
            Always show this preview when joining
          </label>
        </div>
        <div className="custom-modal-footer">
          <button className="custom-modal-btn cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="custom-modal-btn confirm-btn" onClick={handleConfirm} disabled={!mediaReady}>Join</button>
        </div>
      </div>
    </div>
  );
}
